import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Bell } from "lucide-react";

const POLL_MS = 30000;

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function NotificationBell() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/me/notifications");
        if (!cancelled) setData(r.data || { items: [], unread: 0 });
      } catch {
        // silent — user may have signed out
      }
    };
    load();
    const t = setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [user]);

  if (!user) return null;

  const onOpenChange = async (isOpen) => {
    setOpen(isOpen);
    if (isOpen && data.unread > 0) {
      try {
        await api.post("/me/notifications/mark-read");
        setData((d) => ({ ...d, unread: 0 }));
      } catch {
        // ignore
      }
    }
  };

  const go = (n) => {
    setOpen(false);
    if (n.job_id) nav(`/jobs/${n.job_id}`);
  };

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="notif-bell"
          className="relative w-9 h-9 grid place-items-center rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
          aria-label="Notifications"
        >
          <Bell className="w-4 h-4" />
          {data.unread > 0 && (
            <span
              data-testid="notif-unread-count"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-orange-600 text-white text-[10px] font-semibold grid place-items-center border-2 border-white"
            >
              {data.unread > 9 ? "9+" : data.unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel>
          <div className="text-xs uppercase tracking-wider text-slate-500">Notifications</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {data.items.length === 0 ? (
          <div className="p-4 text-sm text-slate-500" data-testid="notif-empty">
            You&apos;re all caught up.
          </div>
        ) : (
          data.items.slice(0, 8).map((n) => (
            <DropdownMenuItem
              key={n.id}
              data-testid={`notif-item-${n.id}`}
              className="flex-col items-start gap-0.5 py-2.5 cursor-pointer"
              onClick={() => go(n)}
            >
              <div className="flex items-center gap-2 w-full">
                {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-orange-600 flex-shrink-0" />}
                <span className="text-sm font-medium text-slate-900 truncate">{n.title}</span>
              </div>
              <div className="text-xs text-slate-600 truncate w-full">{n.body}</div>
              <div className="text-[11px] text-slate-400">{timeAgo(n.created_at)}</div>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
